import { settings } from '@alme/ui-web/settings/settings-util';

import 'regenerator-runtime/runtime';

// Mono Repo Modules
import ClientCommunicator from '@alme/ui-web/ref/clientcommunicator';
import HistoryAppender from '@alme/ui-web/ref/history-appender-maskedinput';
import Initialization from '@alme/ui-web/ref/initialize';
import LaunchMessageProcessor from '@alme/ui-web/ref/launch-message-processor';
import MobileDeviceDetector from '@alme/ui-web/ref/mobile-device-detector';
import PersistentStorage from '@alme/ui-web/ref/persistence';
import PersistenceStrategy from '@alme/ui-web/ref/persistence-strategy-cookie';
import StepOutIcon from '@alme/ui-web/ref/renderer-step-out-icon';

import getHistoryWatcher from '@alme/ui-web/ref/history-watcher';
import InitializeGetHistory from '@alme/ui-web/ref/initialization-gethistory';
import InitializeGetConfiguration from '@alme/ui-web/ref/initialization-get-configuration';
import InputboxLockMutator from '@alme/ui-web/ref/inputbox-lock-mutator';
import InputBoxAutoFocus from '@alme/ui-web/ref/inputbox-autofocus';
import { setupAlmeWindow } from './setup-window-components';
import { setupConversation } from './setup-conversation-components';
import SingleLinkRenderer from '@alme/ui-web/ref/single-link-renderer';
import StateTriggerer from "@alme/ui-web/modules/state-triggerer/src/StateTriggerer";

// Mono Repo - Alight Specific
import Skinner from '@alme/ui-web/ref/skinner';
import StateCopier from '@alme/ui-web/ref/state-copier';
import AlmeNotificationsApi from '@alme/ui-web/ref/alme-notificationsapi';
import LiveChat from '@alme/ui-web/ref/livechat-foundation';

// Custom Modules - Core Overrides
import AutoNavUrl from '../modules/auto-nav-url-aon/auto-nav-url-aon';

// Custom Modules - Alight Specific
import ChatBubbleHistoryAppender from '../modules/advocacy-chat-bubble/chat-bubble-history-appender';
import Advocacy from '../modules/advocacy/advocacy';
import RequestProcessorChatAllowed from '../modules/request-processor-chat-allowed/request-processor-chat-allowed';
import EmbeddInputHandler from '../modules/custom-embed-input-handler/embed-input-handler';
import InitializeDataHandler from '../modules/initialization-data-handler/initialization-data-handler';
import CustomLanguageHandler from '../modules/custom-language-watcher/custom-language-watcher';
import AccessKeyHandler from '../modules/access-key-processor/access-key-processor';
import GenerateNewSession from '../modules/generate-new-session/nextit-generate-new-session';
import ChangeRoleIdProcessor from '../modules/role-id-window-post/changeRoleIdProcessor';
import ProcessLocalePostMessage from '../modules/process-locale-post/changeLocaleProcessor';
import UserDetailsPostMessageHandler from '../modules/user-details-post-message/user-details-post-message-handler';
import PlatformIdProcessor from '../modules/platform-id-processor/platform-id-processor';
import SessionIdOverrideProcessor from '../modules/session-id-override-processor/session-id-override-processor';
import LaunchOffersEventUtility from '../modules/launch-offers-appevent-utility/launchOffersAppEventUtility';
import LaunchOffersProcessor from '../modules/launch-offers-post-message-handling/LaunchOffersProcessor';
import MissedChatCounter from '../modules/live-chat-foundation-missed-count/live-chat-foundation-missed-count';
import ProcessLocale from '../modules/process-locale/process-locale';
import CustomWelcomeUserAppEvent from '../modules/custom-appevent-welcomeuser-onlaunch/custom-appevent-welcomeuser-onlaunch';
import RevokeSimplifiedAnnualEnrollmentEligibilityPostMessageHandler from '../modules/simplified-annual-enrollment-post-message-handlers/src/revokeSimplifiedAnnualEnrollmentEligibilityPostMessageHandler';
import SimplifiedAeFlowProcessUpdatePostMessage from '../modules/simplified-annual-enrollment-post-message-handlers/src/simplifiedAeFlowProcessUpdatePostMessage';
import SearchInputProcessor from '../modules/process-search-input-post/searchInputProcessor';

function setupComponents(conversation){
    const components = {};

    //initialization steps
    components.customLanguageSetup = new CustomLanguageHandler(settings);
    components.persistenceStrategy = new PersistenceStrategy();
    components.initializeGetHistory = new InitializeGetHistory();
    components.initializeGetConfiguration = new InitializeGetConfiguration();
    components.welcomeUserAppevent = new CustomWelcomeUserAppEvent();
    components.almeNotificationsApi = new AlmeNotificationsApi();
    components.missedChatCounter = new MissedChatCounter(settings);

    components.accessKeyHandler = new AccessKeyHandler(settings);
    components.initializationDataHandler = new InitializeDataHandler(settings);
    components.requestProcessorChatAllowed = new RequestProcessorChatAllowed(settings);
    components.embeddInputHandler = new EmbeddInputHandler(settings);
    
    components.changeRoleIdProcessor = new ChangeRoleIdProcessor(settings);
    components.changeLocaleProcessor = new ProcessLocalePostMessage(settings);
    components.searchInputProcessor = new SearchInputProcessor(settings);
    components.platformIdProcessor = new PlatformIdProcessor(settings);
    components.sessionIdOverrideProcessor = new SessionIdOverrideProcessor(settings);
    components.liveChatFoundation = new LiveChat();
    components.generateNewSession = new GenerateNewSession(settings, components.liveChatFoundation._state);
    components.launchOffersEventUtility = new LaunchOffersEventUtility(settings);
    components.launchOffersProcessor = new LaunchOffersProcessor(settings, components.launchOffersEventUtility);
    components.processLocale = new ProcessLocale(settings);
    components.revokeSimplifiedAnnualEnrollmentEligibilityPostMessageHandler = new RevokeSimplifiedAnnualEnrollmentEligibilityPostMessageHandler(settings);
    components.userDetailsPostMessageHandler = new UserDetailsPostMessageHandler(settings);
    components.simplifiedAeFlowProcessUpdatePostMessage = new SimplifiedAeFlowProcessUpdatePostMessage(settings);

    const initializationSteps = {
        onLoad: [
            components.persistenceStrategy,
            components.customLanguageSetup,
            components.platformIdProcessor,
            components.sessionIdOverrideProcessor,
            components.processLocale,
        ],
        onLaunch: [
            components.accessKeyHandler,
            components.initializationDataHandler,
            components.initializeGetConfiguration,
            components.initializeGetHistory,
            components.generateNewSession,
            components.liveChatFoundation.Poller,
            components.welcomeUserAppevent,
            components.almeNotificationsApi._initialization,
            components.requestProcessorChatAllowed,
        ],
    };

    //other components
    components.autoNavUrl = new AutoNavUrl(settings);
    components.clientCommunicator = new ClientCommunicator();
    components.historyAppender = new HistoryAppender();
    const initialization = new Initialization(initializationSteps);
    components.launchMessageProcessor = new LaunchMessageProcessor();
    components.mobileDeviceDetector = new MobileDeviceDetector();
    components.persistentStorage = new PersistentStorage();
    components.historyWatcher = getHistoryWatcher();
    components.stepOutIcon = new StepOutIcon();
    components.inputboxLockMutator = new InputboxLockMutator();
    components.inputBoxAutoFocus = new InputBoxAutoFocus();
    components.stateTriggerer = new StateTriggerer();
    components.singleLinkRenderer = new SingleLinkRenderer(settings, getHistoryWatcher);
    components.chatBubbleHistoryAppender = new ChatBubbleHistoryAppender(settings);
    components.generateNewSession.setupSessionGenerator(initialization);
    components.skinner = new Skinner(settings);
    components.stateCopier = new StateCopier(settings);

    components.advocacy = new Advocacy(settings);

    Object.assign(components, setupAlmeWindow(components),
        setupConversation(components, conversation));

    return {
        components,
        initialization,
    };
}

export {
    setupComponents
};



// WEBPACK FOOTER //
// ./src/setup-components.js